import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import bookingConfigService from "./bookingConfigService";

const initialState = {
  settings: null,
  rules: [],
  overrides: [],
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: "",
};

const getErrorMessage = (error) => error?.message || error?.code || String(error);

export const fetchBookingConfig = createAsyncThunk(
  "bookingConfig/fetch",
  async (_, thunkAPI) => {
    const requests = [
      ["bookingSettings", bookingConfigService.getBookingSettings()],
      ["availabilityRules", bookingConfigService.getAvailabilityRules()],
      ["availabilityOverrides", bookingConfigService.getAvailabilityOverrides()],
    ];

    const results = await Promise.allSettled(requests.map(([, request]) => request));
    const failedIndex = results.findIndex((result) => result.status === "rejected");

    if (failedIndex !== -1) {
      const [collectionName] = requests[failedIndex];
      return thunkAPI.rejectWithValue(
        `${collectionName}: ${getErrorMessage(results[failedIndex].reason)}`
      );
    }

    return {
      settings: results[0].value,
      rules: results[1].value,
      overrides: results[2].value,
    };
  }
);

export const saveBookingConfig = createAsyncThunk(
  "bookingConfig/save",
  async ({ settings, rules, overrides }, thunkAPI) => {
    try {
      const savedSettings = await bookingConfigService.saveBookingSettings(settings);
      let savedRules;
      let savedOverrides;

      try {
        savedRules = await bookingConfigService.saveAvailabilityRules(rules, savedSettings.userId);
      } catch (error) {
        return thunkAPI.rejectWithValue(`availabilityRules: ${getErrorMessage(error)}`);
      }

      try {
        savedOverrides = await bookingConfigService.saveAvailabilityOverrides(overrides, savedSettings.userId);
      } catch (error) {
        return thunkAPI.rejectWithValue(`availabilityOverrides: ${getErrorMessage(error)}`);
      }

      return { settings: savedSettings, rules: savedRules, overrides: savedOverrides };
    } catch (error) {
      return thunkAPI.rejectWithValue(`bookingSettings: ${getErrorMessage(error)}`);
    }
  }
);

const bookingConfigSlice = createSlice({
  name: "bookingConfig",
  initialState,
  reducers: {
    resetBookingConfig: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBookingConfig.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(fetchBookingConfig.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.settings = action.payload.settings;
        state.rules = action.payload.rules;
        state.overrides = action.payload.overrides;
      })
      .addCase(fetchBookingConfig.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(saveBookingConfig.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(saveBookingConfig.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.settings = action.payload.settings;
        state.rules = action.payload.rules;
        state.overrides = action.payload.overrides;
      })
      .addCase(saveBookingConfig.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  },
});

export const { resetBookingConfig } = bookingConfigSlice.actions;
export default bookingConfigSlice.reducer;
